import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FormattedMessage } from 'react-intl';
import Button from '@/components/Button/Button';
import DownloadIcon from '@/assets/download.svg?react';
import { useNotifications } from '@/hooks/useNotifications';
import { fetchComplianceCoverageExport } from '@/lib/api';
import { saveBlobAsFile } from '@/lib/download';
import { coverageExportFileName } from './ComplianceCoverageAdminPageHelpers';

type ComplianceCoverageExportButtonProps = {
  q?: string;
  marketId?: number;
};

const ComplianceCoverageExportButton = ({
  q,
  marketId,
}: ComplianceCoverageExportButtonProps) => {
  const { showError } = useNotifications();

  // A mutation, not a query: the 1h query cache would return a stale file.
  const { mutate: exportCoverage, isPending } = useMutation<
    Blob,
    AxiosError,
    void
  >({
    mutationFn: () =>
      fetchComplianceCoverageExport({
        search: q || undefined,
        market_id: marketId,
      }),
    onSuccess: (blob) => saveBlobAsFile(blob, coverageExportFileName()),
    onError: (error) => {
      showError(
        error.response?.status === 403
          ? 'User lacks required permission'
          : 'Failed to export compliance coverage',
      );
    },
  });

  return (
    <StyledButton
      variant="outlined"
      onClick={() => exportCoverage()}
      disabled={isPending}
      startIcon={
        isPending ? <CircularProgress size={16} /> : <DownloadIcon />
      }
      aria-label="Export"
    >
      <FormattedMessage
        id="complianceCoverage.export"
        defaultMessage="Export"
      />
    </StyledButton>
  );
};

const StyledButton = styled(Button)({
  textWrap: 'nowrap',

  '.MuiButton-startIcon svg': {
    width: 16,
    height: 16,
  },

  // download.svg hardcodes a white stroke for dark surfaces.
  '.MuiButton-startIcon svg path': {
    stroke: 'currentColor',
  },
});

export default ComplianceCoverageExportButton;
